import { Button, Divider, FIELD_WIDTH, Icon, IconButton, strIsNullOrEmpty, Typography, usePopoverContext } from '@fox/atoms';
import { FlexColumnSpaced, FlexRowSpaced, Padded, VerticalInset } from '@fox/layout';
import { getExtension } from 'fox-extensions';
import React from 'react';
import styled from 'styled-components';
import { GraphColumn, QueryFilter, useAutoDataGridContext } from '../../auto-data-grid';
import { FilterOperatorProps } from '../../contracts/filters';
import { FilterField } from './field';
import { FilterComponent } from './filterComponent';
import { FilterModifier } from './modifier';

export type LocalStateFilterObject = {
	dataIndex: string;
	filterType: string;
	constrT: string;
	constV: string | string[];
};

export type FilterConditionItemProps<T> = {
	filter: LocalStateFilterObject;
	onRemove: (index: number) => void;
	onAddNewFilter: () => void;
	onValueChange: (e: unknown, i: number) => void;
	totalFilterCount: number;
	index: number;
	operator: FilterOperatorProps;
	filterOperatorsExt: (filterType: string | undefined) => FilterOperatorProps;
	appliedFilters: LocalStateFilterObject[];
	setAppliedFilters: (f: LocalStateFilterObject[]) => void;
	availableColumns: GraphColumn<T>[] | GraphColumn<T>;
	onModifierChange: (e: string, i: number) => void;
	filterParams?: unknown;
};

export const PopoverFilterConditionsGrid = styled.div<{ $columnsIsArray: boolean }>`
	display: grid;
	gap: 1rem;
	grid-template-columns: ${({ $columnsIsArray }) =>
		$columnsIsArray ? `3rem ${FIELD_WIDTH} repeat(2, ${FIELD_WIDTH}) 3rem` : `repeat(2, ${FIELD_WIDTH}) 3rem`};
	column-gap: 1rem;
	align-items: flex-start;
	width: 100%;
`;

export function FilterConditionItem<T>({
	filter,
	totalFilterCount,
	index,
	operator,
	availableColumns,
	onRemove,
	onAddNewFilter,
	onValueChange,
	filterOperatorsExt,
	appliedFilters,
	setAppliedFilters,
	onModifierChange
}: FilterConditionItemProps<T>) {
	const isFirst = index === 0;
	const isLast = totalFilterCount - 1 === index;
	const hasOtherFilters = totalFilterCount > 1;
	const columnsIsArray = Array.isArray(availableColumns);
	return (
		<div>
			<FlexRowSpaced>
				<PopoverFilterConditionsGrid $columnsIsArray={columnsIsArray}>
					{columnsIsArray ? (
						<>
							<div style={{ paddingTop: '0.5rem' }}>
								<Typography variant="body1">{isFirst ? 'Where' : 'And'}</Typography>
							</div>
							<FilterField
								availableColumns={availableColumns}
								filter={filter}
								filterOperatorsExt={filterOperatorsExt}
								index={index}
								appliedFilters={appliedFilters}
								setAppliedFilters={setAppliedFilters}
							/>
						</>
					) : null}
					<FilterModifier filter={filter} operator={operator} onChange={e => onModifierChange(e, index)} />
					<FilterComponent
						filter={filter}
						editFilter={e => onValueChange(e, index)}
						filterParams={
							Array.isArray(availableColumns)
								? availableColumns.find(c => c.dataIndex === filter.dataIndex)?.filterParams
								: availableColumns?.filterParams
						}
					/>
					{hasOtherFilters ? (
						<IconButton
							iconName="Close"
							title="Remove"
							onClick={() => {
								onRemove(index);
							}}
						/>
					) : (
						<div></div>
					)}
				</PopoverFilterConditionsGrid>
			</FlexRowSpaced>
			{isLast ? (
				<VerticalInset>
					<Button style={{ color: '#272727' }} onClick={() => onAddNewFilter()}>
						<Icon iconName={'Add'} />
						Add a new filter
					</Button>
				</VerticalInset>
			) : null}
		</div>
	);
}

export function FilterPopover<T extends Record<string, any>>(props: { columns: GraphColumn<T>[] | GraphColumn<T> }) {
	return <FilterPopoverInner columns={props.columns} />;
}

export function FilterPopoverInner<T extends Record<string, any>>(props: { columns: GraphColumn<T>[] | GraphColumn<T> }) {
	const { filters, setFilters } = useAutoDataGridContext();
	const { closePopover } = usePopoverContext();
	const { columns } = props;

	let filterType;
	let dataIndex;
	let columnFilters;
	let otherFilters: null | QueryFilter[] = null;

	if (!Array.isArray(columns)) {
		filterType = columns.filterType;
		dataIndex = columns.dataIndex;
		columnFilters = filters.filter(el => el.dataIndex === dataIndex);
		otherFilters = filters.filter(el => el.dataIndex !== dataIndex);
	}
	const filterOperatorsExt = filterType => getExtension<FilterOperatorProps>(`column-operators-${filterType}`);

	const defaulFilter = filterOperatorsExt?.(filterType)?.filterConstraints?.[0];

	let currentFilters: LocalStateFilterObject[] = [];

	if (!Array.isArray(columns)) {
		if (columnFilters.length === 0) {
			currentFilters = [{ dataIndex, filterType, constrT: defaulFilter ?? '', constV: '' }];
		} else {
			currentFilters = columnFilters?.map(f => {
				const [key, value] = Object.entries(JSON.parse(f.filterValue))[0];
				return { dataIndex: f.dataIndex, filterType: f.filterType, constrT: key, constV: value as string };
			});
		}
	} else {
		if (filters.length === 0) {
			currentFilters = [{ dataIndex: '', filterType: '', constrT: '', constV: '' }];
		} else if (filters.length > 0) {
			currentFilters = filters?.map(f => {
				const [key, value] = Object.entries(JSON.parse(f.filterValue))[0];
				return { dataIndex: f.dataIndex, filterType: f.filterType, constrT: key, constV: value as string };
			});
		}
	}
	const [appliedFilters, setAppliedFilters] = React.useState<LocalStateFilterObject[]>(currentFilters);

	React.useEffect(() => setAppliedFilters(currentFilters), [filters]);

	const handleAddRow = () => {
		if (!Array.isArray(columns)) {
			setAppliedFilters([...appliedFilters, { dataIndex, filterType, constrT: defaulFilter ?? '', constV: '' }]);
		} else setAppliedFilters([...appliedFilters, { dataIndex: '', filterType: '', constrT: '', constV: '' }]);
	};

	const handleRemoveRow = (i: number) => {
		const newList = [...appliedFilters];
		newList.splice(i, 1);
		setAppliedFilters(newList);
	};

	const handleConstVChange = (v: string | string, i: number) => {
		const list = [...appliedFilters];
		list[i].constV = v;
		setAppliedFilters(list);
	};

	function handleConstraintChange(e, i: number) {
		const list = [...appliedFilters];
		list[i].constrT = e;
		setAppliedFilters(list);
	}

	function isDuplicate(entry: QueryFilter, arr: QueryFilter[]) {
		return arr.some(x => entry.dataIndex === x.dataIndex && entry.filterValue === x.filterValue);
	}

	function updateFilters(arr2: QueryFilter[], arr1?: QueryFilter[]) {
		let newArray: QueryFilter[] = [];
		for (const entry of arr2) {
			if (!isDuplicate(entry, newArray)) {
				newArray.push(entry);
			}
		}
		if (!Array.isArray(columns) && arr1) {
			setFilters([...arr1.filter(f => f.dataIndex !== dataIndex), ...newArray]);
		} else setFilters(newArray);
	}

	return (
		<Padded>
			<FlexColumnSpaced>
				<Typography variant="h6">Filtering</Typography>
				<FlexColumnSpaced style={{ padding: '0 1rem ' }}>
					{appliedFilters.map((singleRow, index) => {
						return (
							<FilterConditionItem
								key={index}
								index={index}
								filter={singleRow}
								totalFilterCount={appliedFilters.length}
								availableColumns={columns}
								operator={filterOperatorsExt(singleRow.filterType)}
								filterOperatorsExt={filterOperatorsExt}
								onAddNewFilter={handleAddRow}
								onValueChange={handleConstVChange}
								onRemove={handleRemoveRow}
								appliedFilters={appliedFilters}
								setAppliedFilters={setAppliedFilters}
								onModifierChange={handleConstraintChange}
							/>
						);
					})}
				</FlexColumnSpaced>
				<Divider />
				<FlexRowSpaced>
					<Button
						color="error"
						onClick={() => {
							setFilters([]);
						}}
					>
						Clear all
					</Button>
					<Button
						variant="primary"
						style={{ marginLeft: 'auto' }}
						onClick={() => {
							const newFilters = appliedFilters
								.filter(
									filter =>
										!strIsNullOrEmpty(filter.constrT) &&
										!strIsNullOrEmpty(filter.dataIndex) &&
										!strIsNullOrEmpty(filter.filterType)
								)
								.map(f => {
									return {
										dataIndex: f.dataIndex,
										filterType: f.filterType,
										filterValue: JSON.stringify({ [f.constrT]: f.constV })
									};
								});
							// Preserve the other filters
							if (otherFilters) newFilters.push(...otherFilters);
							updateFilters(newFilters);
							closePopover(true);
						}}
					>
						Apply filter
					</Button>
				</FlexRowSpaced>
			</FlexColumnSpaced>
		</Padded>
	);
}
